/**
 * Helpers de formatação de data PT-BR.
 *
 * REGRA: toda data exibida ao usuário DEVE usar estes helpers.
 * Nunca usar `toLocaleDateString()` cru ou `format()` do date-fns
 * sem `{ locale: ptBR }` para texto exposto ao usuário final.
 *
 * Formatos técnicos (ISO `yyyy-MM-dd`, `yyyy-MM`) continuam válidos
 * para params de API, value de selects e chaves de URL — use
 * `toISODate` / `toISOMonth` para gerá-los.
 */
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type DateInput = Date | string | number | null | undefined;

const FALLBACK = '—';

/** Normaliza entrada para Date. Trata `"YYYY-MM-DD"` sem timezone-shift. */
function toDate(input: DateInput): Date | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) return isValid(input) ? input : null;
  if (typeof input === 'number') {
    const d = new Date(input);
    return isValid(d) ? d : null;
  }
  // string
  const s = input.trim();
  if (!s) return null;
  // YYYY-MM-DD puro → ancora em meia-noite local para evitar shift de fuso
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    return isValid(d) ? d : null;
  }
  // YYYY-MM → primeiro dia do mês
  if (/^\d{4}-\d{2}$/.test(s)) {
    const d = new Date(`${s}-01T00:00:00`);
    return isValid(d) ? d : null;
  }
  // ISO completa ou outros formatos parseáveis
  const isoParsed = parseISO(s);
  if (isValid(isoParsed)) return isoParsed;
  const fallback = new Date(s);
  return isValid(fallback) ? fallback : null;
}

function fmt(input: DateInput, pattern: string, fallback = FALLBACK): string {
  const d = toDate(input);
  if (!d) return fallback;
  try {
    return format(d, pattern, { locale: ptBR });
  } catch {
    return fallback;
  }
}

/** `29/05/2026` — data curta padrão. */
export const formatDate = (d: DateInput, fallback = FALLBACK) => fmt(d, 'dd/MM/yyyy', fallback);

/** `29/05/2026 14:32` — data + hora. */
export const formatDateTime = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, 'dd/MM/yyyy HH:mm', fallback);

/** `29/05/2026 14:32:07` — data + hora + segundos (logs/auditoria). */
export const formatDateTimeWithSeconds = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, 'dd/MM/yyyy HH:mm:ss', fallback);

/** `29 de maio de 2026` — data por extenso. */
export const formatDateLong = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, "dd 'de' MMMM 'de' yyyy", fallback);

/** `maio/2026` — competência mensal compacta. */
export const formatMonth = (d: DateInput, fallback = FALLBACK) => fmt(d, 'MMMM/yyyy', fallback);

/** `maio de 2026` — competência por extenso. */
export const formatMonthLong = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, "MMMM 'de' yyyy", fallback);

/** `05/2026` — competência numérica. */
export const formatMonthShort = (d: DateInput, fallback = FALLBACK) => fmt(d, 'MM/yyyy', fallback);

/** `maio 2026` — mês por extenso + ano (sem barra, sem "de"). */
export const formatMonthYear = (d: DateInput, fallback = FALLBACK) => fmt(d, 'MMMM yyyy', fallback);

/** `mai/26` — competência compacta para gráficos/badges. */
export const formatMonthCompact = (d: DateInput, fallback = FALLBACK) => fmt(d, 'MMM/yy', fallback);

/** `29/05/2026 14:32 às HH:mm` simplificado → use `formatDateTime`. Helper específico para `dd/MM/yyyy 'às' HH:mm`. */
export const formatDateAtTime = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, "dd/MM/yyyy 'às' HH:mm", fallback);

/** `14:32` — apenas hora. */
export const formatTime = (d: DateInput, fallback = FALLBACK) => fmt(d, 'HH:mm', fallback);

/** `há 3 dias` / `há cerca de 2 horas`. */
export function formatRelative(d: DateInput, fallback = FALLBACK): string {
  const date = toDate(d);
  if (!date) return fallback;
  try {
    return formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
  } catch {
    return fallback;
  }
}

/** `sex, 29/05/2026` — dia da semana + data curta. */
export const formatWeekdayDate = (d: DateInput, fallback = FALLBACK) =>
  fmt(d, 'EEE, dd/MM/yyyy', fallback);

// ===== Helpers técnicos (entrada para API/DB/URL) =====

/** `2026-05-29` (ISO date, para API/params). Use SOMENTE para valores técnicos. */
export function toISODate(d: DateInput): string | null {
  const date = toDate(d);
  return date ? format(date, 'yyyy-MM-dd') : null;
}

/** `2026-05-01` — primeiro dia do mês (campo `mes_referencia`). Use SOMENTE para valores técnicos. */
export function toISOMonth(d: DateInput): string | null {
  const date = toDate(d);
  return date ? format(date, 'yyyy-MM-01') : null;
}
